/**
 * Original vector artwork for ad creatives (no third-party assets), referenced by `artKey`. The signature scenes
 * (NightRoadScene, StorefrontArt) live in @runnerboy/ui so every app can use them.
 */
import { NightRoadScene, StorefrontArt } from '@runnerboy/ui';
import type { ReactElement } from 'react';

export { NightRoadScene, StorefrontArt };

type ArtProps = { className?: string; accent?: string };

// ---- ad creative art -----------------------------------------------------------------------------------------

function Shield({ className, accent = '#FFAA1F' }: ArtProps) {
  return (
    <svg viewBox="0 0 96 96" className={className} aria-hidden>
      <path d="M48 8 L80 20 V46 C80 66 66 80 48 88 C30 80 16 66 16 46 V20 Z" fill="#0E1324" />
      <path d="M48 16 L72 25 V46 C72 61 62 72 48 79 C34 72 24 61 24 46 V25 Z" fill="#1D2D70" />
      <path d="M36 48 L45 57 L62 38" fill="none" stroke={accent} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Checklist({ className, accent = '#1D2D70' }: ArtProps) {
  return (
    <svg viewBox="0 0 96 96" className={className} aria-hidden>
      <rect x="18" y="10" width="60" height="78" rx="12" fill="#FFFFFF" stroke="#D4D7E0" strokeWidth="3" />
      <rect x="34" y="4" width="28" height="12" rx="6" fill={accent} />
      {[30, 48, 66].map((y) => (
        <g key={y}>
          <circle cx="34" cy={y} r="6" fill={accent} />
          <path d={`M31 ${y} l2 2.5 l4 -5`} stroke="#FFAA1F" strokeWidth="2" fill="none" strokeLinecap="round" />
          <rect x="46" y={y - 3} width="22" height="6" rx="3" fill="#D4D7E0" />
        </g>
      ))}
    </svg>
  );
}

function Ice({ className, accent = '#8FD3FF' }: ArtProps) {
  return (
    <svg viewBox="0 0 160 120" className={className} aria-hidden>
      {[
        [30, 46, -12],
        [72, 30, 8],
        [96, 64, -4],
        [48, 74, 14],
      ].map(([x, y, r], i) => (
        <g key={i} transform={`translate(${x} ${y}) rotate(${r})`}>
          <rect width="38" height="38" rx="10" fill="#FFFFFF" fillOpacity="0.9" stroke={accent} strokeWidth="2.5" />
          <rect x="4" y="4" width="30" height="30" rx="8" fill={accent} fillOpacity="0.22" />
          <path d="M9 10 H22" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" />
        </g>
      ))}
      {[18, 130, 140, 24].map((x, i) => (
        <path key={i} d={`M${x} ${20 + i * 22} l3 -6 l3 6 l-3 6 z`} fill="#FFFFFF" opacity="0.95" />
      ))}
    </svg>
  );
}

function Bottle({ body, label, cap }: { body: string; label: string; cap: string }) {
  return (
    <>
      <rect x="15" y="2" width="10" height="10" rx="2" fill={cap} />
      <path d="M15 12 H25 V28 Q34 36 34 48 V100 Q34 106 28 106 H12 Q6 106 6 100 V48 Q6 36 15 28 Z" fill={body} />
      <rect x="8" y="56" width="24" height="26" rx="3" fill={label} />
      <path d="M11 40 Q12 34 16 32" stroke="#FFFFFF" strokeOpacity="0.55" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    </>
  );
}

function Mixers({ className }: ArtProps) {
  return (
    <svg viewBox="0 0 160 120" className={className} aria-hidden>
      <g transform="translate(24 10)">
        <Bottle body="#12B76A" label="#E8F8F0" cap="#FFAA1F" />
      </g>
      <g transform="translate(64 4)">
        <Bottle body="#E28C00" label="#FFF0CF" cap="#1D2D70" />
      </g>
      <g transform="translate(106 16)">
        <Bottle body="#2C56C9" label="#EDF2FD" cap="#E5484D" />
      </g>
      {[30, 70, 120].map((x, i) => (
        <circle key={x} cx={x} cy={18 + i * 8} r="3" fill="#FFFFFF" opacity="0.75" />
      ))}
    </svg>
  );
}

function Tonic({ className }: ArtProps) {
  return (
    <svg viewBox="0 0 160 120" className={className} aria-hidden>
      <path d="M56 26 H104 L96 108 Q95 114 89 114 H71 Q65 114 64 108 Z" fill="#FFFFFF" fillOpacity="0.6" stroke="#12B76A" strokeWidth="3" />
      <path d="M60 50 H100 L95 106 Q94 110 89 110 H71 Q66 110 65 106 Z" fill="#B9EBCF" />
      <circle cx="96" cy="30" r="16" fill="#C8E66A" stroke="#7FA31F" strokeWidth="3" />
      <path d="M96 16 V44 M82 30 H110" stroke="#7FA31F" strokeWidth="2" />
      {[70, 80, 88, 76].map((x, i) => (
        <circle key={i} cx={x} cy={64 + i * 10} r="2.5" fill="#FFFFFF" />
      ))}
    </svg>
  );
}

function Snacks({ className }: ArtProps) {
  return (
    <svg viewBox="0 0 160 120" className={className} aria-hidden>
      <ellipse cx="80" cy="98" rx="62" ry="12" fill="#4A2A00" opacity="0.18" />
      <path d="M22 70 Q80 122 138 70 Z" fill="#C9781A" />
      <path d="M22 70 H138" stroke="#A35F10" strokeWidth="4" strokeLinecap="round" />
      {[
        [44, 58, -20],
        [64, 50, 15],
        [86, 56, -8],
        [106, 52, 25],
        [74, 64, 40],
        [96, 66, -30],
      ].map(([x, y, r], i) => (
        <path key={i} transform={`translate(${x} ${y}) rotate(${r})`} d="M-10 0 C-10 -12 10 -14 12 -4 C13 2 6 4 2 1 C-2 -2 -6 4 -10 0 Z" fill="#F4D9A6" stroke="#C9A066" strokeWidth="1.5" />
      ))}
    </svg>
  );
}

function Beer({ className }: ArtProps) {
  return (
    <svg viewBox="0 0 160 120" className={className} aria-hidden>
      <path d="M50 30 H100 V104 Q100 112 92 112 H58 Q50 112 50 104 Z" fill="#FFAA1F" />
      <path d="M100 46 H112 Q122 46 122 58 V78 Q122 90 112 90 H100" fill="none" stroke="#FFF0CF" strokeWidth="7" />
      <path d="M46 30 Q50 14 64 18 Q72 6 86 14 Q102 10 104 26 Q108 34 100 36 H50 Q42 36 46 30 Z" fill="#FFF8EA" />
      {[60, 72, 84, 66, 90].map((x, i) => (
        <circle key={i} cx={x} cy={56 + (i % 3) * 16} r="2.5" fill="#FFF4DC" opacity="0.85" />
      ))}
    </svg>
  );
}

function Whisky({ className }: ArtProps) {
  return (
    <svg viewBox="0 0 160 120" className={className} aria-hidden>
      <path d="M48 44 H112 L106 106 Q105 112 99 112 H61 Q55 112 54 106 Z" fill="#FFFFFF" fillOpacity="0.25" stroke="#E8B54A" strokeWidth="3" />
      <path d="M51 70 H109 L106 104 Q105 108 100 108 H60 Q55 108 54 104 Z" fill="#C2771C" />
      <rect x="66" y="58" width="22" height="22" rx="4" fill="#FFFFFF" fillOpacity="0.6" transform="rotate(-12 77 69)" />
    </svg>
  );
}

function Scooter({ className, accent = '#FFAA1F' }: ArtProps) {
  return (
    <svg viewBox="0 0 120 80" className={className} aria-hidden>
      <path d="M6 40 H30 M0 50 H24" stroke={accent} strokeWidth="4" strokeLinecap="round" opacity="0.75" />
      <circle cx="40" cy="62" r="11" fill="#0E1324" stroke="#FFFFFF" strokeWidth="3" />
      <circle cx="96" cy="62" r="11" fill="#0E1324" stroke="#FFFFFF" strokeWidth="3" />
      <path d="M40 62 L58 38 H84 L96 62" fill="none" stroke="#FFFFFF" strokeWidth="7" strokeLinejoin="round" />
      <rect x="46" y="18" width="26" height="22" rx="5" fill={accent} />
      <circle cx="80" cy="14" r="8" fill="#FFFFFF" />
    </svg>
  );
}

function Store({ className }: ArtProps) {
  return <StorefrontArt name="Runner Boy" className={className} />;
}

function Water({ className }: ArtProps) {
  return (
    <svg viewBox="0 0 160 120" className={className} aria-hidden>
      {[34, 70, 106].map((x, i) => (
        <g key={x} transform={`translate(${x} ${14 + (i % 2) * 8})`}>
          <rect x="7" y="0" width="12" height="8" rx="2" fill="#2C56C9" />
          <path d="M7 8 H19 V16 Q27 20 27 30 V90 Q27 96 21 96 H5 Q-1 96 -1 90 V30 Q-1 20 7 16 Z" fill="#CFE3FF" stroke="#8DB7F2" strokeWidth="2" />
          {[34, 48, 80].map((y) => (
            <rect key={y} x="-1" y={y} width="28" height="2.5" fill="#FFFFFF" opacity="0.85" />
          ))}
          <rect x="1" y="54" width="24" height="18" rx="2" fill="#2C56C9" />
        </g>
      ))}
    </svg>
  );
}

const ART: Record<string, (p: ArtProps) => ReactElement> = {
  'road-night': ({ className }) => <NightRoadScene className={className} />,
  shield: Shield,
  checklist: Checklist,
  ice: Ice,
  mixers: Mixers,
  tonic: Tonic,
  snacks: Snacks,
  beer: Beer,
  whisky: Whisky,
  scooter: Scooter,
  store: Store,
  water: Water,
};

export function Art({ name, className, accent }: { name: string | null | undefined; className?: string; accent?: string }) {
  const C = (name && ART[name]) || null;
  return C ? <C className={className} accent={accent} /> : null;
}

/** Back-compat export (Road Safety page). */
export function RoadNight({ className }: ArtProps) {
  return <NightRoadScene className={className} />;
}
